/**
 * registration-modal.ts - Tournament registration modal
 *
 * Handles tournament registration UI with Stripe Checkout redirect for payments
 */

import { registerForTournament, getEntryFees } from '../api/registration-api';
import { fetchProfile } from '../api/profile-api';
import { escapeHtml } from '../utils/security';
import { getCurrentUser } from '../auth/auth-state';
import { openAuthModal } from '../auth/auth-modals';
import { t } from '../utils/i18n-helper';

// Track if tournament requires payment
let currentTournamentRequiresPayment = false;
// Track payment options for current tournament
let currentTournamentHasStripe = false;
let currentTournamentAllowsOnsitePayment = false;

/**
 * Get the appropriate rating based on tournament time control
 */
function getRatingForTimeControl(
  profile: { rating?: number; ratingStd?: number; ratingRapid?: number; ratingBlitz?: number },
  timeControl: string | undefined
): number | undefined {
  switch (timeControl) {
    case 'blitz':
      return profile.ratingBlitz || profile.ratingRapid || profile.ratingStd || profile.rating;
    case 'rapid':
      return profile.ratingRapid || profile.ratingStd || profile.ratingBlitz || profile.rating;
    case 'classical':
    default:
      return profile.ratingStd || profile.ratingRapid || profile.ratingBlitz || profile.rating;
  }
}

// Store current tournament's time control for display
let currentTournamentTimeControl: string | undefined;

/**
 * Open registration modal for a tournament
 * @param tournamentId - Tournament ID
 * @param tournamentName - Tournament name
 * @param entryFees - Array of entry fee options (optional, will be fetched if not provided)
 * @param timeControl - Tournament time control (blitz/rapid/classical)
 * @param hasStripe - Whether tournament has Stripe connected
 * @param allowOnsitePayment - Whether tournament allows on-site payment
 */
export async function openRegistrationModal(
  tournamentId: string,
  tournamentName: string,
  entryFees: any[] = [],
  timeControl?: string,
  hasStripe?: boolean,
  allowOnsitePayment?: boolean
): Promise<void> {
  currentTournamentTimeControl = timeControl;
  currentTournamentHasStripe = hasStripe || false;
  currentTournamentAllowsOnsitePayment = allowOnsitePayment || false;

  // Check if user is logged in
  const currentUser = getCurrentUser();

  if (!currentUser) {
    // User not logged in - show login modal
    openAuthModal('login');
    return;
  }

  try {
    // Check if user has a profile
    const profile = await fetchProfile();

    if (!profile) {
      alert('You need to complete your profile before registering for tournaments.');
      window.location.href = '/profile';
      return;
    }

    // Check profile completeness
    if (!profile.firstName) {
      alert('Please complete your profile before registering.');
      window.location.href = '/profile';
      return;
    }

    // Fetch entry fees from API if not provided or empty
    let fees = entryFees;
    if (!fees || fees.length === 0) {
      try {
        fees = await getEntryFees(tournamentId);
      } catch {
        fees = [];
      }
    }

    // Determine if payment is required based on actual entry fees
    currentTournamentRequiresPayment = fees && fees.length > 0;

    // Show registration modal
    showRegistrationModal(tournamentId, tournamentName, profile, fees);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load profile';
    showError(message);
  }
}

/**
 * Show registration modal
 */
function showRegistrationModal(
  tournamentId: string,
  tournamentName: string,
  profile: any,
  entryFees: any[]
): void {
  const modal = document.getElementById('registration-modal');
  if (!modal) {
    createRegistrationModal();
    return showRegistrationModal(tournamentId, tournamentName, profile, entryFees);
  }

  // Update modal content
  const titleEl = modal.querySelector('#registration-modal-title');
  const profileInfoEl = modal.querySelector('#registration-profile-info');
  const entryFeesEl = modal.querySelector('#registration-entry-fees');
  const paymentInfoEl = modal.querySelector('#registration-payment-info');
  const registerBtn = modal.querySelector('#register-submit-btn') as HTMLButtonElement;

  if (titleEl) {
    titleEl.textContent = `Register for ${tournamentName}`;
  }

  if (profileInfoEl) {
    const displayRating = getRatingForTimeControl(profile, currentTournamentTimeControl);
    profileInfoEl.innerHTML = `
      <div class="profile-info-box">
        <h4>Your Registration Information</h4>
        <p><strong>Name:</strong> ${escapeHtml(profile.firstName)} ${escapeHtml(profile.lastName || '')}</p>
        ${displayRating ? `<p><strong>Rating:</strong> ${displayRating}</p>` : ''}
        ${profile.title ? `<p><strong>Title:</strong> ${escapeHtml(profile.title)}</p>` : ''}
        ${profile.federationCode ? `<p><strong>Federation:</strong> ${escapeHtml(profile.federationCode)}</p>` : ''}
        ${profile.club ? `<p><strong>Club:</strong> ${escapeHtml(profile.club)}</p>` : ''}
        <p class="profile-edit-link">
          <a href="/profile" target="_blank">Edit profile</a>
        </p>
      </div>
    `;
  }

  if (entryFeesEl) {
    if (entryFees && entryFees.length > 0) {
      entryFeesEl.innerHTML = `
        <h4>Entry Fee</h4>
        <div class="entry-fee-options">
          ${entryFees.map(fee => `
            <label class="entry-fee-option">
              <input type="radio" name="entry-fee" value="${fee.id}" ${entryFees.length === 1 ? 'checked' : ''}>
              <div class="fee-details">
                <span class="fee-name">${escapeHtml(fee.description)}</span>
                <span class="fee-amount">$${(fee.amount / 100).toFixed(2)}</span>
              </div>
            </label>
          `).join('')}
        </div>
      `;
    } else {
      entryFeesEl.innerHTML = '<p>No entry fee required</p>';
    }
  }

  // Show payment options if required
  if (paymentInfoEl) {
    if (currentTournamentRequiresPayment) {
      if (currentTournamentHasStripe && currentTournamentAllowsOnsitePayment) {
        // Both options available - show choice
        paymentInfoEl.innerHTML = `
          <div class="payment-method-choice">
            <h4>${t('registration.paymentMethod', 'Payment Method')}</h4>
            <div class="payment-options">
              <label class="payment-option">
                <input type="radio" name="payment-method" value="online" checked>
                <div class="payment-option-details">
                  <span class="payment-option-name">${t('registration.payOnline', 'Pay online')}</span>
                  <span class="payment-option-desc">${t('registration.payOnlineDesc', 'Pay now via Stripe (credit card)')}</span>
                </div>
              </label>
              <label class="payment-option">
                <input type="radio" name="payment-method" value="cash">
                <div class="payment-option-details">
                  <span class="payment-option-name">${t('registration.payAtTournament', 'Pay at tournament')}</span>
                  <span class="payment-option-desc">${t('registration.payAtTournamentDesc', 'Pay cash when you arrive')}</span>
                </div>
              </label>
            </div>
          </div>
        `;
        paymentInfoEl.style.display = 'block';
      } else if (currentTournamentHasStripe) {
        // Only online payment
        paymentInfoEl.innerHTML = `
          <p class="payment-redirect-info">
            <small>${t('registration.stripeRedirect', 'You will be redirected to Stripe\'s secure payment page to complete your registration.')}</small>
          </p>
        `;
        paymentInfoEl.style.display = 'block';
      } else {
        // Only cash payment (no Stripe connected)
        paymentInfoEl.innerHTML = `
          <p class="payment-info-cash">
            <small>${t('registration.cashPaymentInfo', 'Entry fee will be paid at the tournament.')}</small>
          </p>
        `;
        paymentInfoEl.style.display = 'block';
      }
    } else {
      paymentInfoEl.innerHTML = '';
      paymentInfoEl.style.display = 'none';
    }
  }

  // Update button text based on payment scenario
  if (registerBtn) {
    if (currentTournamentRequiresPayment) {
      if (currentTournamentHasStripe && !currentTournamentAllowsOnsitePayment) {
        registerBtn.textContent = t('registration.continueToPayment', 'Continue to Payment');
      } else if (currentTournamentHasStripe && currentTournamentAllowsOnsitePayment) {
        registerBtn.textContent = t('registration.confirmRegistration', 'Confirm Registration');
      } else {
        registerBtn.textContent = t('registration.confirmRegistration', 'Confirm Registration');
      }
    } else {
      registerBtn.textContent = t('registration.confirmRegistration', 'Confirm Registration');
    }
    registerBtn.onclick = () => handleRegistration(tournamentId);
  }

  // Show modal
  modal.style.display = 'flex';
}

/**
 * Create registration modal HTML
 */
function createRegistrationModal(): void {
  const modalHTML = `
    <div id="registration-modal" class="modal" style="display: none;">
      <div class="modal-content">
        <span class="close" onclick="closeRegistrationModal()">&times;</span>
        <h2 id="registration-modal-title">Register for Tournament</h2>

        <div id="registration-profile-info"></div>
        <div id="registration-entry-fees"></div>
        <div id="registration-payment-info" style="display: none;"></div>

        <div id="registration-error" class="error-message"></div>
        <div id="registration-success" class="success-message"></div>

        <div class="form-actions">
          <button type="button" id="register-submit-btn" class="btn btn-primary">Confirm Registration</button>
          <button type="button" class="btn btn-secondary" onclick="closeRegistrationModal()">Cancel</button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Close registration modal
 */
export function closeRegistrationModal(): void {
  const modal = document.getElementById('registration-modal');
  if (modal) {
    modal.style.display = 'none';

    // Clear messages
    const errorEl = modal.querySelector('#registration-error');
    const successEl = modal.querySelector('#registration-success');
    if (errorEl) errorEl.textContent = '';
    if (successEl) successEl.textContent = '';
  }
}

/**
 * Handle tournament registration
 */
async function handleRegistration(tournamentId: string): Promise<void> {
  const modal = document.getElementById('registration-modal');
  if (!modal) return;

  const errorEl = modal.querySelector('#registration-error') as HTMLElement;
  const successEl = modal.querySelector('#registration-success') as HTMLElement;
  const submitBtn = modal.querySelector('#register-submit-btn') as HTMLButtonElement;

  // Clear messages
  if (errorEl) errorEl.textContent = '';
  if (successEl) successEl.textContent = '';

  // Get selected entry fee
  const selectedFeeInput = modal.querySelector('input[name="entry-fee"]:checked') as HTMLInputElement;
  const selectedFeeId = selectedFeeInput?.value;

  // Get selected payment method (if choice is available)
  const selectedPaymentMethodInput = modal.querySelector('input[name="payment-method"]:checked') as HTMLInputElement;
  const paymentMethod = selectedPaymentMethodInput?.value || (currentTournamentHasStripe ? 'online' : 'cash');

  // Validate fee selection if payment required
  if (currentTournamentRequiresPayment && !selectedFeeId) {
    if (errorEl) errorEl.textContent = t('registration.selectFee', 'Please select an entry fee option');
    return;
  }

  // Determine button text during processing
  const isOnlinePayment = paymentMethod === 'online' && currentTournamentHasStripe;

  // Disable submit button
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = isOnlinePayment
      ? t('registration.redirectingToPayment', 'Redirecting to payment...')
      : t('registration.registering', 'Registering...');
  }

  try {
    // Create registration on backend with payment method
    const result = await registerForTournament(tournamentId, selectedFeeId, paymentMethod) as any;

    // If payment is required, redirect to Stripe Checkout
    if (result.requiresPayment && result.checkoutUrl) {
      // Redirect to Stripe Checkout
      window.location.href = result.checkoutUrl;
      return;
    }

    // Cash payment at tournament
    if (result.cashPayment) {
      if (successEl) {
        successEl.innerHTML = `
          <div class="success-message-content">
            <strong>Registration submitted!</strong><br>
            Entry fee: ${result.feeAmount}<br>
            <small>To be paid at the tournament.</small>
          </div>
        `;
      }

      // Redirect after short delay
      setTimeout(() => {
        closeRegistrationModal();
        window.location.href = '/tournaments?registered=1';
      }, 2000);
      return;
    }

    // No payment required - registration complete
    if (successEl) {
      successEl.textContent = 'Registration successful!';
    }

    // Redirect after short delay
    setTimeout(() => {
      closeRegistrationModal();
      window.location.href = '/tournaments?registered=1';
    }, 1500);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to register for tournament';

    if (errorEl) {
      errorEl.textContent = message;
    }

    // Re-enable submit button
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = currentTournamentRequiresPayment
        ? 'Continue to Payment'
        : 'Confirm Registration';
    }
  }
}

/**
 * Show error message
 */
function showError(message: string): void {
  if (typeof window.showError === 'function') {
    window.showError(message);
  } else {
    alert('Error: ' + message);
  }
}

// Export to window for onclick handlers
window.openRegistrationModal = openRegistrationModal;
window.closeRegistrationModal = closeRegistrationModal;
